/**
 * TBMuktBharat My Submissions Page
 * Users can view all their competition submissions
 */

import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { GovLayout, GovPageTitle, GovCard, GovButton } from '@/components/layout/GovLayout'
import {
  Trophy, FileText, CheckCircle, XCircle, Clock, Eye, Award,
  Calendar, MapPin, Building, AlertCircle, Loader2
} from 'lucide-react'
import { motion } from 'framer-motion'
import competitionService, { CompetitionSubmission, SubmissionStatus } from '@/services/competitionService'
import { useAuthStore } from '@/stores/authStore'
import { Badge, EmptyState } from '@/components/ui/premium-components'

// ================================================================================
// STATUS BADGE COMPONENT
// ================================================================================
const StatusBadge: React.FC<{ status: SubmissionStatus }> = ({ status }) => {
  const config = {
    PENDING: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
    APPROVED: { label: 'Approved', color: 'bg-green-100 text-green-700 border-green-300' },
    REJECTED: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-300' },
  }

  const { label, color } = config[status]

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${color}`}>
      {status === 'PENDING' && <Clock className="w-3 h-3" />}
      {status === 'APPROVED' && <CheckCircle className="w-3 h-3" />}
      {status === 'REJECTED' && <XCircle className="w-3 h-3" />}
      {label}
    </span>
  )
}

// ================================================================================
// TYPE ICON COMPONENT
// ================================================================================
const TypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const icons: Record<string, string> = {
    POSTER: '🎨',
    QUIZ: '🧠',
    ESSAY: '📝',
    DRAWING: '🖌️',
    SLOGAN: '💬',
    DEBATE: '🎤',
    PHOTOGRAPHY: '📷',
  }

  return <span className="text-2xl">{icons[type] || '🏆'}</span>
}

// ================================================================================
// MAIN COMPONENT
// ================================================================================

const MySubmissionsPage: React.FC = () => {
  const { isAuthenticated } = useAuthStore()
  const [submissions, setSubmissions] = useState<CompetitionSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<SubmissionStatus | ''>('')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }
    fetchSubmissions()
  }, [isAuthenticated, filterStatus, page])

  const fetchSubmissions = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await competitionService.getUserSubmissions({
        page,
        size: 10,
      })

      let filtered = response.content
      if (filterStatus) {
        filtered = filtered.filter(s => s.status === filterStatus)
      }

      setSubmissions(filtered)
      setTotalPages(response.totalPages)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load submissions')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  if (!isAuthenticated) {
    return (
      <GovLayout>
        <div className="max-w-4xl mx-auto">
          <GovCard className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Login Required</h2>
            <p className="text-gray-600 mb-4">Please login to view your submissions</p>
            <Link to="/login" className="text-blue-600 hover:underline">
              Go to Login
            </Link>
          </GovCard>
        </div>
      </GovLayout>
    )
  }

  return (
    <GovLayout>
      <div className="max-w-6xl mx-auto">
        <GovPageTitle
          title="My Submissions"
          subtitle="Track your competition entries and results"
        />

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setFilterStatus('')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              filterStatus === ''
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Submissions
          </button>
          <button
            onClick={() => setFilterStatus('PENDING')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              filterStatus === 'PENDING'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Under Review
          </button>
          <button
            onClick={() => setFilterStatus('APPROVED')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              filterStatus === 'APPROVED'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Approved
          </button>
          <button
            onClick={() => setFilterStatus('REJECTED')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              filterStatus === 'REJECTED'
                ? 'bg-red-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Rejected
          </button>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : error ? (
          <GovCard className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">{error}</p>
            <GovButton variant="primary" onClick={fetchSubmissions} className="mt-4">
              Try Again
            </GovButton>
          </GovCard>
        ) : submissions.length === 0 ? (
          <EmptyState
            icon={<Trophy className="w-16 h-16 text-neutral-300" />}
            title="No Submissions Yet"
            description="You haven't submitted any competition entries. Browse competitions to get started!"
            action={
              <Link to="/competitions">
                <GovButton variant="primary">Browse Competitions</GovButton>
              </Link>
            }
          />
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {submissions.map((submission, index) => (
              <motion.div
                key={submission.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <GovCard className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    {/* Left: Competition Info */}
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-blue-50">
                        <TypeIcon type={submission.competitionType || 'POSTER'} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {submission.competitionTitle}
                        </h3>
                        {submission.submissionTitle && (
                          <p className="text-sm text-gray-600 mt-1">
                            "{submission.submissionTitle}"
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(submission.submittedAt || '')}
                          </span>
                          {submission.schoolCollegeName && (
                            <span className="flex items-center gap-1">
                              <Building className="w-4 h-4" />
                              {submission.schoolCollegeName}
                            </span>
                          )}
                          {submission.district && submission.state && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {submission.district}, {submission.state}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: Status & Actions */}
                    <div className="flex flex-col items-end gap-2">
                      <StatusBadge status={submission.status} />

                      {/* Rank if approved */}
                      {submission.status === 'APPROVED' && submission.rank && (
                        <div className="flex items-center gap-1 text-amber-600 font-medium">
                          <Award className="w-5 h-5" />
                          <span>Rank #{submission.rank}</span>
                        </div>
                      )}

                      {/* Feedback if available */}
                      {submission.feedback && (
                        <p className="text-sm text-gray-600 max-w-xs text-right">
                          <span className="font-medium">Feedback:</span> {submission.feedback}
                        </p>
                      )}

                      {/* Certificate Link */}
                      {submission.certificateUrl && (
                        <a
                          href={submission.certificateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
                        >
                          <Award className="w-4 h-4" />
                          View Certificate
                        </a>
                      )}

                      {/* File Link */}
                      {submission.fileUrl && (
                        <a
                          href={submission.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                        >
                          <Eye className="w-4 h-4" />
                          View Submission
                          {submission.fileSize && (
                            <span className="text-gray-400">
                              ({formatFileSize(submission.fileSize)})
                            </span>
                          )}
                        </a>
                      )}
                    </div>
                  </div>
                </GovCard>
              </motion.div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-gray-600">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Quick Links */}
        <div className="mt-8 flex gap-4">
          <Link
            to="/competitions"
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Trophy className="w-4 h-4" />
            Browse Competitions
          </Link>
        </div>
      </div>
    </GovLayout>
  )
}

export default MySubmissionsPage